import React, { useState, useEffect } from 'react';
import { Card, ListGroup, Form, Button, Badge, Row, Col, Dropdown } from 'react-bootstrap';
import { Send, User, MessageSquare, Bell, Zap } from 'lucide-react';
import { io } from 'socket.io-client';

const socket = io("http://localhost:5000");

const CANNED_RESPONSES = [
    { label: "Greeting", text: "Hello! 👋 Thank you for contacting PeoplesDock. How can I help you today?" },
    { label: "Pricing", text: "Our pricing starts at ₹199/employee. You can view full details on our pricing page." },
    { label: "Demo", text: "I'd love to show you a demo! Are you free for a 15-minute call tomorrow?" },
    { label: "Technical", text: "I am forwarding your query to our technical team. They will get back to you shortly." },
    { label: "Closing", text: "Is there anything else I can help you with today? Have a great day!" }
];

const AdminChatPanel = () => {
    const [activeChats, setActiveChats] = useState([]); 
    const [selectedChat, setSelectedChat] = useState(null); 
    const [messages, setMessages] = useState([]);
    const [reply, setReply] = useState("");

    useEffect(() => {
        socket.on('admin_receive_message', (data) => {
            setActiveChats(prev => {
                const exists = prev.find(c => c.id === data.socket_id);
                
                if (exists) {
                    return prev.map(c => {
                        if (c.id === data.socket_id) {
                            return {
                                ...c,
                                lastMsg: data.message,
                                unreadCount: selectedChat?.id === c.id ? 0 : (c.unreadCount || 0) + 1
                            };
                        }
                        return c;
                    });
                }
                return [...prev, { id: data.socket_id, lastMsg: data.message, unreadCount: 1 }];
            });

            if (selectedChat?.id === data.socket_id) {
                setMessages(prev => [...prev, { text: data.message, sender: 'visitor' }]);
            }

            // Play notification sound
            const audio = new Audio('/sounds/notification-beep.mp3');
            audio.play().catch(() => {});
        });

        return () => socket.off('admin_receive_message');
    }, [selectedChat]);

    const handleSelectChat = (chat) => {
        setSelectedChat(chat);
        setActiveChats(prev => prev.map(c => 
            c.id === chat.id ? { ...c, unreadCount: 0 } : c
        ));
    };

    const handleSendReply = () => {
        if (!reply.trim() || !selectedChat) return;
        socket.emit('admin_message', {
            visitorSocketId: selectedChat.id,
            message: reply
        });
        setMessages(prev => [...prev, { text: reply, sender: 'admin' }]);
        setReply("");
    };

    return (
        <Card className="shadow-sm border-0" style={{ height: '600px' }}>
            <Row className="g-0 h-100">
                <Col md={4} className="border-end h-100 overflow-auto bg-white">
                    <Card.Header className="bg-white py-3 border-bottom d-flex justify-content-between align-items-center">
                        <span className="fw-bold">Live Conversations</span>
                        <Bell size={16} className="text-muted" />
                    </Card.Header>
                    <ListGroup variant="flush">
                        {activeChats.map(chat => (
                            <ListGroup.Item 
                                key={chat.id} 
                                action 
                                active={selectedChat?.id === chat.id}
                                onClick={() => handleSelectChat(chat)}
                                className="py-3 border-bottom"
                            >
                                <div className="d-flex align-items-center gap-3">
                                    <div className="bg-light p-2 rounded-circle"><User size={18} className="text-primary" /></div>
                                    <div className="text-truncate flex-grow-1">
                                        <div className="d-flex justify-content-between">
                                            <span className="fw-bold small">Visitor {chat.id.substring(0, 5)}</span>
                                            {chat.unreadCount > 0 && <Badge pill bg="danger" style={{ fontSize: '0.7rem' }}>{chat.unreadCount}</Badge>}
                                        </div>
                                        <div className="text-muted small text-truncate">{chat.lastMsg}</div>
                                    </div>
                                </div>
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                </Col>

                <Col md={8} className="d-flex flex-column h-100 bg-light">
                    {selectedChat ? (
                        <>
                            <Card.Header className="bg-white py-3 shadow-sm z-1 d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center gap-2">
                                    <Badge bg="success" pill style={{ width: '10px', height: '10px', padding: 0 }}> </Badge>
                                    <span className="fw-bold">Chatting with {selectedChat.id.substring(0, 8)}</span>
                                </div>
                                <Dropdown onSelect={(text) => setReply(text)}>
                                    <Dropdown.Toggle variant="outline-secondary" size="sm" className="d-flex align-items-center gap-1">
                                        <Zap size={14} className="text-warning" /> Quick Reply
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu align="end">
                                        {CANNED_RESPONSES.map((res, idx) => (
                                            <Dropdown.Item key={idx} eventKey={res.text} className="small">
                                                <strong>{res.label}</strong>
                                            </Dropdown.Item>
                                        ))}
                                    </Dropdown.Menu>
                                </Dropdown>
                            </Card.Header>
                            <Card.Body className="overflow-auto p-4 d-flex flex-column">
                                {messages.map((m, i) => (
                                    <div key={i} className={`mb-3 ${m.sender === 'admin' ? 'ms-auto text-end' : 'me-auto text-start'}`}>
                                        <div className={`p-3 rounded-4 shadow-sm ${m.sender === 'admin' ? 'bg-primary text-white' : 'bg-white'}`} style={{ maxWidth: '85%' }}>
                                            {m.text}
                                        </div>
                                    </div>
                                ))}
                            </Card.Body>
                            <Card.Footer className="bg-white border-top p-3">
                                <Form className="d-flex gap-2">
                                    <Form.Control 
                                        className="border-0 bg-light py-2"
                                        placeholder="Write your response..." 
                                        value={reply}
                                        onChange={(e) => setReply(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleSendReply())}
                                    />
                                    <Button variant="primary" onClick={handleSendReply}><Send size={18} /></Button>
                                </Form>
                            </Card.Footer>
                        </>
                    ) : (
                        <div className="m-auto text-center">
                            <MessageSquare size={64} className="text-muted opacity-25 mb-3" />
                            <h5 className="text-muted fw-bold">No Chat Selected</h5>
                        </div>
                    )}
                </Col>
            </Row>
        </Card>
    );
};

export default AdminChatPanel;